import React, { useState } from 'react';
import { CardSection, SimpleCard } from '@/components/Cards';
import theme from './BuyerOptionPresetCard.module.scss';
import { getOptionPresetImageThumbnailUrl as getOptionPresetImageThumbnailUrl } from '@/services/option-preset/option-preset-image';
import { useOptionPresetDelete } from '@/services/option-preset';
import { BuyerOptionPresetCardEdit } from './BuyerOptionPresetCardEdit';
import { faEdit, faGripDotsVertical, faHistory, faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import { MarkdownDisplay, TextView, Thumbnail } from '@ygtechnologies/bygr-frontend-component-library';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { ProjectPermissionToggle } from '@/components/permissions';
import { ProjectCurrencyDisplay } from '../../currency';
import HistoryLogModal from '../../HistoryLog/HistoryLogModal';

import type { OptionPresetDto } from '@/services/option-preset';
import { Card, Dropdown, DropdownContextMenu } from '@ygtechnologies/rubus-ui';
interface BuyerOptionPresetCardProps {
    preset: OptionPresetDto;
    handleProps: object;
    onEditClick?: () => void;
    onDeleteClick?: () => void;
}

const BuyerOptionPresetCard: React.FC<BuyerOptionPresetCardProps> = ({
    preset,
    handleProps,
    onEditClick,
    onDeleteClick,
}) => {
    const [isInEditMode, setIsInEditMode] = useState(false);
    const [historyModalIsOpen, setHistoryModalIsOpen] = useState(false);
    const deletePresetMigration = useOptionPresetDelete(preset.optionPresetGroupId);
    const handleDeleteClick = async () => {
        onDeleteClick?.();
        await deletePresetMigration.mutateAsync(preset.id);
    };

    if (isInEditMode) {
        return (
            <SimpleCard>
                <BuyerOptionPresetCardEdit
                    preset={preset}
                    onEditSave={() => {
                        setIsInEditMode(false);
                    }}
                    onEditCancel={() => setIsInEditMode(false)}
                />
            </SimpleCard>
        );
    }

    return (
        <Card className={theme.card} variant="default">
            <div className={theme.dragHandle} {...handleProps}>
                <FontAwesomeIcon icon={faGripDotsVertical} size="lg" />
            </div>
            <div className={theme.topSection}>
                <div className={theme.imageWrapper}>
                    <Thumbnail
                        draggable={false}
                        src={preset.images.length > 0 ? getOptionPresetImageThumbnailUrl(preset.images[0].id) : null}
                    />
                </div>
                <div className={theme.rightSection}>
                    <div className={theme.info}>
                        <CardSection caption={<Trans>Name</Trans>}>{preset.name}</CardSection>
                        <CardSection caption={<Trans>Direct cost</Trans>} width="200px" right>
                            {preset.hasDirectCost ? <ProjectCurrencyDisplay value={preset.price!} /> : <Trans>None</Trans>}
                        </CardSection>
                    </div>
                    <CardSection caption={<Trans>Description</Trans>}>
                        <TextView numberOfLines={8}>
                            <MarkdownDisplay className="text-view-markdown-fixer" markdown={preset.description} />
                        </TextView>
                    </CardSection>
                </div>
            </div>

            <ProjectPermissionToggle condition="isAdministrator">
                <div className={theme.dropdownWrapper}>
                    <DropdownContextMenu>
                        <Dropdown.Item
                            icon={faEdit}
                            onClick={() => {
                                onEditClick?.();
                                setIsInEditMode(true);
                            }}>
                            <Trans>Edit style</Trans>
                        </Dropdown.Item>
                        <Dropdown.Confirm
                            dialogTitle={<Trans>Delete style</Trans>}
                            dialogBody={<Trans>Are you sure you want to delete style "{preset.name}"?</Trans>}
                            confirmButton={{ children: <Trans>Delete</Trans> }}
                            icon={faTrashAlt}
                            onConfirm={handleDeleteClick}>
                            <Trans>Delete style</Trans>
                        </Dropdown.Confirm>
                        <Dropdown.Item onClick={() => setHistoryModalIsOpen(true)} icon={faHistory}>
                            <Trans>View log</Trans>
                        </Dropdown.Item>
                    </DropdownContextMenu>
                </div>
            </ProjectPermissionToggle>
            {/* <CardTags tags={preset.tags} /> */}

            <HistoryLogModal
                filter={{ projectId: preset.projectId, objectType: 'OptionPreset', objectIds: [preset.id] }}
                isOpen={historyModalIsOpen}
                toggle={() => setHistoryModalIsOpen(false)}
            />
        </Card>
    );
};

export default BuyerOptionPresetCard;
